import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, ClipboardList, Eye, TrendingUp } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { methodology } from '../data/mock';
import '../styles/pages.css';
import usePageMeta from '../hooks/usePageMeta';

const phaseIcons = {
  1: ClipboardList,
  2: Eye,
  3: TrendingUp
};

const Methodology = () => {
  usePageMeta({
    title: 'Metodología',
    description: 'Evaluación de riesgo con 35 criterios en tres secciones ponderadas —Eventos, Seguridad y Protección Civil— y dictamen operativo documentado.',
    path: '/methodology',
  });

  return (
    <Layout>
      {/* Page Header */}
      <section className="page-header">
        <div className="container">
          <h1>Metodología SOEM</h1>
          <p>Marco de trabajo integral para la gestión sistemática de seguridad</p>
        </div>
      </section>

      {/* Introduction */}
      <section className="section">
        <div className="container">
          <div className="intro-content">
            <p className="lead-text">{methodology.intro}</p>
          </div>
        </div>
      </section>

      {/* Phases */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Fases del Proceso</h2>
            <p className="section-subtitle">
              Un enfoque estructurado que cubre todas las etapas del evento
            </p>
          </div>
          <div className="phases-grid">
            {methodology.phases.map((phase) => {
              const IconComponent = phaseIcons[phase.id];
              return (
                <div key={phase.id} className="phase-card">
                  <div className="phase-header">
                    <div className="phase-number">{phase.id}</div>
                    <div className="phase-icon">
                      {IconComponent && <IconComponent size={24} />}
                    </div>
                  </div>
                  <span className="phase-name">{phase.name}</span>
                  <h3>{phase.title}</h3>
                  <ul className="phase-items">
                    {phase.items.map((item, index) => (
                      <li key={index}>
                        <CheckCircle size={14} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Principios Fundamentales</h2>
          </div>
          <div className="principles-grid">
            {methodology.principles.map((principle, index) => (
              <div key={index} className="principle-card">
                <div className="principle-number">{index + 1}</div>
                <p>{principle}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documentation */}
      <section className="section section-alt">
        <div className="container">
          <div className="content-grid">
            <div className="content-text">
              <h2>Documentación y Trazabilidad</h2>
              <p>
                Cada decisión, cada análisis y cada acción queda documentada. 
                La trazabilidad completa permite no solo rendir cuentas, sino 
                también aprender y mejorar continuamente.
              </p>
              <p>
                Nuestros reportes ejecutivos proporcionan visibilidad clara 
                sobre el estado de seguridad antes, durante y después del evento, 
                facilitando la toma de decisiones informadas.
              </p>
              <Link to="/reports" className="btn btn-outline">
                Ver tipos de reportes
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="content-stats">
              <div className="stat-card">
                <span className="stat-label">Análisis previo</span>
                <span className="stat-value">100%</span>
                <span className="stat-desc">de eventos evaluados antes de iniciar</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Documentación</span>
                <span className="stat-value">Completa</span>
                <span className="stat-desc">trazabilidad de decisiones</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Aplique nuestra metodología a su evento</h2>
            <p>
              Contáctenos para conocer cómo podemos adaptar nuestro enfoque 
              a las necesidades específicas de su próximo evento.
            </p>
            <Link to="/contact" className="btn btn-primary">
              Solicitar información
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Methodology;
